import React, { useRef, useState, useMemo } from "react";
import { Box, Typography, Dialog, DialogContent, IconButton, TextField, Button, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Formik, Form, FormikHelpers } from "formik";
import * as Yup from "yup";
import axios from "axios";
import CustomCaptcha from "./CustomCaptcha";
import { useTranslation } from "react-i18next";
import { useBackButtonHandler } from "../../hooks/useBackButtonHandler";

const fieldSx = {
  mb: 2.5,
  "& .MuiOutlinedInput-root": {
    borderRadius: 0,
    fontFamily: '"Calibri", sans-serif',
    fontSize: "0.95rem",
    "& fieldset": { borderColor: "rgba(29,47,65,0.2)" },
    "&:hover fieldset": { borderColor: "rgba(29,47,65,0.4)" },
    "&.Mui-focused fieldset": { borderColor: "#1D2F41", borderWidth: "1px" },
  },
  "& .MuiInputLabel-root": {
    fontFamily: '"Calibri", sans-serif',
    fontSize: "0.95rem",
    color: "rgba(29,47,65,0.5)",
    "&.Mui-focused": { color: "#1D2F41" },
  },
};

interface FormValues { name: string; email: string; message: string; captcha: string; }

interface Props {
  open: boolean;
  onClose: () => void;
}

const ContactMessageDialog: React.FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const captchaAnswerRef = useRef("");
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  // Handle back button to close modal
  useBackButtonHandler(open, () => {
    onClose();
    setSubmitted(false);
    setSubmitError(false);
  }, "message-dialog");

  const validationSchema = useMemo(() => Yup.object({
    name: Yup.string().required(t("contact.form.validation.nameRequired")),
    email: Yup.string().email(t("contact.form.validation.emailInvalid")).required(t("contact.form.validation.emailRequired")),
    message: Yup.string().required(t("contact.form.validation.messageRequired")),
    captcha: Yup.string().required(t("contact.form.validation.captchaRequired")),
  }), [t]);

  const handleClose = () => { onClose(); setSubmitted(false); setSubmitError(false); };

  const handleSubmit = async (values: FormValues, { resetForm, setFieldError }: FormikHelpers<FormValues>) => {
    if (values.captcha.toLowerCase() !== captchaAnswerRef.current.toLowerCase()) {
      setFieldError("captcha", t("contact.form.validation.captchaWrong")); return;
    }
    try {
      const response = await axios.post("https://api.kanonics.com/api/send-email", { from_name: values.name, from_email: values.email, message: values.message, user_referrer: document.referrer });
      console.log("RESPONSE", response);
      if (response.data.result.success) { setSubmitted(true); resetForm(); } else { setSubmitError(true); }
    } catch { setSubmitError(true); }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth sx={{ zIndex: 99999 }} PaperProps={{ sx: { borderRadius: 0, bgcolor: "#f8f9fb", boxShadow: "0 24px 64px rgba(29,47,65,0.12)" } }}>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", px: 4, pt: 4, pb: 3, borderBottom: "1px solid rgba(29,47,65,0.12)" }}>
          <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", fontFamily: '"Calibri", sans-serif', color: "#1D2F41", bgcolor: "#EBEDF0", display: "inline-block", px: 1.25, py: 0.75 }}>
            {t("contact.form.dialogTitle")}
          </Typography>
          <IconButton onClick={handleClose} aria-label={t("nav.closeDialog")} sx={{ color: "rgba(29,47,65,0.4)", mt: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ px: 4, py: 4 }}>
          {submitted ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <Typography sx={{ fontSize: "1.05rem", fontWeight: 500, color: "#1D2F41", fontFamily: '"Calibri", sans-serif', mb: 1 }}>
                {t("contact.form.success")}
              </Typography>
              <Typography sx={{ fontSize: "0.9rem", color: "rgba(29,47,65,0.6)", fontFamily: '"Calibri", sans-serif' }}>
                {t("contact.form.successSub")}
              </Typography>
            </Box>
          ) : (
            <Formik initialValues={{ name: "", email: "", message: "", captcha: "" }} validationSchema={validationSchema} onSubmit={handleSubmit}>
              {({ values, errors, touched, handleChange, handleBlur, isSubmitting, dirty, isValid }) => (
                <Form>
                  <TextField fullWidth name="name" label={t("contact.form.name")} value={values.name} onChange={handleChange} onBlur={handleBlur} error={touched.name && Boolean(errors.name)} helperText={touched.name && errors.name} sx={fieldSx} />
                  <TextField fullWidth name="email" label={t("contact.form.email")} type="email" value={values.email} onChange={handleChange} onBlur={handleBlur} error={touched.email && Boolean(errors.email)} helperText={touched.email && errors.email} sx={fieldSx} />
                  <TextField fullWidth name="message" label={t("contact.form.message")} multiline rows={4} value={values.message} onChange={handleChange} onBlur={handleBlur} error={touched.message && Boolean(errors.message)} helperText={touched.message && errors.message} sx={{ ...fieldSx, mb: 3 }} />
                  <CustomCaptcha
                    value={values.captcha}
                    onChange={handleChange}
                    error={touched.captcha && Boolean(errors.captcha)}
                    helperText={touched.captcha && errors.captcha ? String(errors.captcha) : undefined}
                    onCaptchaGenerated={(text) => { captchaAnswerRef.current = text; }}
                    captchaLabel={t("contact.form.captchaLabel")}
                  />
                  {submitError && (
                    <Typography sx={{ fontSize: "0.85rem", color: "rgba(180,0,0,0.7)", fontFamily: '"Calibri", sans-serif', mb: 2 }}>
                      {t("contact.form.error")}
                    </Typography>
                  )}
                  <Button type="submit" variant="contained" disableElevation disabled={!dirty || !isValid || isSubmitting}
                    endIcon={isSubmitting ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : <span>→</span>}
                    sx={{ bgcolor: "#2b616121", color: "#1D2F41", px: 3, py: 1.5, borderRadius: 0, fontSize: "0.95rem", fontWeight: 500, textTransform: "none", fontFamily: '"Calibri", sans-serif', "&:hover": { bgcolor: "rgba(43,97,97,0.2)" }, "&.Mui-disabled": { bgcolor: "rgba(29,47,65,0.06)", color: "rgba(29,47,65,0.3)" } }}>
                    {t("contact.form.submit")}
                  </Button>
                </Form>
              )}
            </Formik>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ContactMessageDialog;
